import React, { useState } from "react";

import {
  AlertTriangle,
  BadgeCheck,
  Check,
  Circle,
  Eye,
  Loader2,
  Lock,
  Send,
} from "lucide-react";

import { askAboutPlanLine } from "./BriefingStatus";
import { FLOW, planLabel, pubLabel } from "./flowCopy";
import { planFlow } from "./planFlow";
import { planGapOf, planNextStep } from "./planNext";
import { VerifyMark, sectionTitles } from "./ReviewMarks";
import {
  NextStepCard,
  pendingFor,
  PlanNav,
  shortDate,
  STEP_EYEBROW,
  stepHeading,
  underReviewFor,
} from "./shared";
import {
  apiErrorMessage,
  useAccountPlanActions,
  useAccountPlanFile,
} from "../../Hooks/useAccountPlan";
import type { AccountPlanOverview } from "../../Types/AccountPlan";
import { STALLED_LINE } from "../../Utils/AccountPlans/queueText";
import { toast } from "../../Utils/Helper/toast";

interface Props {
  accountId: string;
  data: AccountPlanOverview;
  nav: PlanNav;
  onChanged: () => void;
}

const todayLabel = () => shortDate(new Date().toISOString());

const CheckRow: React.FC<{
  ok: boolean;
  advisory?: boolean;
  title: string;
  testId: string;
  children: React.ReactNode;
}> = ({ ok, advisory, title, testId, children }) => (
  <li className="flex items-start gap-3 py-3" data-testid={testId} data-ok={ok}>
    <span
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
        ok
          ? "bg-emerald-100 text-emerald-700"
          : advisory
            ? "bg-amber-100 text-amber-700"
            : "bg-amber-50 text-amber-600"
      }`}
    >
      {ok ? (
        <Check className="h-3.5 w-3.5" aria-label="done" />
      ) : advisory ? (
        <AlertTriangle className="h-3.5 w-3.5" aria-label="advisory" />
      ) : (
        <Circle className="h-3.5 w-3.5" aria-label="pending" />
      )}
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-medium text-gray-900">{title}</div>
      <div className="mt-0.5 text-xs text-gray-500">{children}</div>
    </div>
  </li>
);

const Header: React.FC<{ name: string; subtitle: string }> = ({
  name,
  subtitle,
}) => (
  <div className="mb-5">
    <div className="flex flex-wrap items-center gap-2">
      <span className={STEP_EYEBROW}>{stepHeading("AE", "publish")}</span>
      <span className="text-xs text-gray-500">
        Release the reviewed briefing to the sponsor
      </span>
    </div>
    <h2 className="mt-1 text-lg font-semibold text-gray-900">
      Publish briefing — {name}
    </h2>
    <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>
  </div>
);

/** "Under review by Jordan Lee (AE) and Priya Patel (CSM) for 12 minutes". */
const underReviewLabel = (
  data: AccountPlanOverview,
  since?: string | null
): string => {
  const pick = (re: RegExp) => data.team.find(m => re.test(m.role))?.name;
  const ae = pick(/^AE$|account exec/i);
  const csm = pick(/^(CSM|TAM|BCS)$|customer success/i);
  const who = [ae && `${ae} (AE)`, csm && `${csm} (CSM)`]
    .filter(Boolean)
    .join(" and ");
  return underReviewFor(since, who || undefined);
};

const PublishTab: React.FC<Props> = ({ accountId, data, nav, onChanged }) => {
  const { acknowledgePlanGaps, acknowledgeNoAnswers, publishBriefing } =
    useAccountPlanActions();
  const fileId = data.current_file_id;
  const { file, reload } = useAccountPlanFile(accountId, fileId);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const rawReview = file?.review || data.current_review || null;
  const review = rawReview && rawReview.status !== "none" ? rawReview : null;
  const name = data.account.name;

  if (!fileId || !data.files.length || !review) {
    // Name the real state (UI tour A item 4): being read, stalled, stopped,
    // waiting on Confirm plan, or the briefing being written. The action is the
    // page's one next step (the header's), or none while the system is working.
    const flow = planFlow(data);
    const gap = planGapOf(data, flow, nav.canAct("AE"));
    const step = planNextStep(data, flow, {
      ae: nav.canAct("AE"),
      csm: nav.canAct("CSM"),
    });
    return (
      <div>
        <Header name={name} subtitle="Nothing to publish yet." />
        <div
          data-testid="publish-empty"
          data-state={gap.kind}
          className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-500"
        >
          <Send className="mx-auto mb-3 h-10 w-10 text-gray-300" aria-hidden />
          {gap.kind === "none" && nav.canAct("AE")
            ? "Upload the account plan first."
            : gap.text}
        </div>
        {step && step.tab !== "publish" && (
          <NextStepCard
            label="Next step"
            action={{
              label: step.label,
              onClick: () =>
                step.extra ? nav.go(step.tab, step.extra) : nav.go(step.tab),
            }}
          >
            {gap.kind === "none" ? "Upload the account plan." : gap.text}
          </NextStepCard>
        )}
      </div>
    );
  }

  const persona = nav.persona;
  const isAE = nav.canAct("AE");
  const published = review.published;
  const owners = data.settings.section_owners;
  const ae = review.progress.AE;
  const csm = review.progress.CSM;
  const aeDone = ae.pending.length === 0;
  const csmDone = csm.pending.length === 0;
  const gapsOk = review.gaps === 0 || !!review.gaps_acknowledged;
  // Briefing questions failed (or never ran): publishing is allowed only once the
  // AE confirms the sponsor will see no answers (spec PUB-03, decision C).
  const noAnswers = !!review.no_answers;
  const noAnswersOk = !noAnswers || !!review.no_answers_acknowledged;
  const blockers = review.publish_blockers || [];
  const canPublish =
    isAE && !published && blockers.length === 0 && gapsOk && noAnswersOk;
  const briefingView = file?.sponsor_view;
  // The plan gaps row in each answer's own words (UI tour, 2026-11-03).
  const hasAnswers =
    briefingView?.briefing_status !== "failed" &&
    !!briefingView?.briefing?.answers?.length;
  const cov = briefingView?.briefing?.coverage;
  const partial = cov?.partial ?? 0;
  const missing = cov?.missing ?? 0;
  const qn = (n: number) => `${n} Briefing Question${n === 1 ? "" : "s"}`;
  // Without the answers loaded, the count alone (review.gaps), in neutral words.
  const split = partial + missing === review.gaps;
  const gapWords = split
    ? [
        partial && `${qn(partial)} partly answered`,
        missing && `${qn(missing)} not in the plan`,
      ]
        .filter(Boolean)
        .join(", ")
    : `${qn(review.gaps ?? 0)} not fully answered`;
  const gapSeen = !split
    ? `${review.gaps === 1 ? "this" : "these"} marked as not fully answered`
    : partial && missing
      ? "these marked partly answered or missing from the plan"
      : missing
        ? `${missing === 1 ? "this" : "these"} marked missing from the plan`
        : `${partial === 1 ? "this" : "these"} marked partly answered`;
  const ownedSections = data.sections.filter(s => owners[s.id]);
  const aeName =
    persona === "AE"
      ? "you"
      : data.team.find(m => /^AE$|account exec/i.test(m.role))?.name ||
        "the AE";

  const afterWrite = async () => {
    await reload();
    onChanged();
  };

  const doPublish = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const res = await publishBriefing(accountId, fileId);
      const rec = res?.review?.published || res?.published;
      const pendingCsm = rec ? rec.csm_pending : csm.pending;
      toast.success({
        title: `Published ${pubLabel(rec) || planLabel(review, "the plan")} to the sponsor`,
        // Says where chat moved too (2026-10-31): the sponsor now asks about this version.
        message: `${
          pendingCsm.length
            ? `Published without CSM confirmation on ${pendingCsm.length} section(s): ${sectionTitles(data, pendingCsm)}.`
            : "All owned sections verified."
        } ${FLOW.askAboutPlan} is now open for your team and the sponsor on ${pubLabel(rec) || planLabel(review, "this version")}.`,
      });
      setConfirming(false);
      await afterWrite();
    } catch (err) {
      const msg = apiErrorMessage(err, "Couldn't publish the briefing.");
      setActionError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const ackGaps = async (checked: boolean) => {
    setBusy(true);
    setActionError(null);
    try {
      await acknowledgePlanGaps(accountId, fileId, checked);
      await afterWrite();
    } catch (err) {
      setActionError(apiErrorMessage(err, "Couldn't update the checklist."));
    } finally {
      setBusy(false);
    }
  };

  const ackNoAnswers = async (checked: boolean) => {
    setBusy(true);
    setActionError(null);
    try {
      await acknowledgeNoAnswers(accountId, fileId, checked);
      await afterWrite();
    } catch (err) {
      setActionError(apiErrorMessage(err, "Couldn't update the checklist."));
    } finally {
      setBusy(false);
    }
  };

  let next: React.ReactNode;
  if (published && isAE && data.freshness?.overdue) {
    // Item 18: an overdue plan isn't "Done": the next step is a new version.
    next = (
      <NextStepCard
        label="Refresh due"
        action={{
          label: FLOW.uploadNewVersion,
          onClick: () => nav.go("plan", { focus: "upload" }),
        }}
      >
        The sponsor still reads the published briefing, but it&apos;s past your
        organization&apos;s refresh date. Upload the updated plan.
      </NextStepCard>
    );
  } else if (published) {
    next = (
      <NextStepCard
        label="Done"
        action={{ label: FLOW.backToPlans, onClick: nav.backToAccounts }}
        notAStep
      >
        The sponsor has the published briefing. Back to Account Plans for
        what&apos;s next.
      </NextStepCard>
    );
  } else if (persona === "CSM" && !csmDone) {
    next = (
      <NextStepCard
        label="CSM next step"
        action={{
          label: "Review briefing",
          onClick: () => nav.go("sponsor"),
        }}
      >
        Confirm {sectionTitles(data, csm.pending)} so the sponsor sees it
        verified.
      </NextStepCard>
    );
  } else if (persona === "AE" && pendingFor(review, "AE").length) {
    next = (
      <NextStepCard
        label="AE next step"
        action={{
          label: "Review briefing",
          onClick: () => nav.go("sponsor"),
        }}
      >
        {ae.pending.length} of your sections still need verification.
      </NextStepCard>
    );
  } else if (persona === "AE" && isAE && !published && !gapsOk) {
    next = (
      <NextStepCard
        label="AE next step"
        action={{
          label: "Acknowledge gaps",
          onClick: () => document.getElementById("ack-gaps")?.focus(),
        }}
      >
        Acknowledge the plan gaps above, then publish.
      </NextStepCard>
    );
  } else if (persona === "AE" && isAE && !published && !noAnswersOk) {
    next = (
      <NextStepCard
        label="AE next step"
        action={{
          label: "Confirm no answers",
          onClick: () => document.getElementById("ack-no-answers")?.focus(),
        }}
      >
        The Briefing Questions weren&apos;t answered for this draft. Regenerate
        the draft to try again, or confirm above that the sponsor will see no
        answers, then publish.
      </NextStepCard>
    );
  } else if (canPublish) {
    // One place to act (STP-12): the checklist's own Publish button (with its
    // confirm step) is the only way to publish, so no second Publish card here.
    next = null;
  } else if (persona === "CSM") {
    next = (
      <NextStepCard
        label="CSM next step"
        action={{ label: FLOW.backToPlans, onClick: nav.backToAccounts }}
        notAStep
      >
        Your sections are confirmed. {aeName === "the AE" ? "The AE" : aeName}{" "}
        publishes when ready.
      </NextStepCard>
    );
  } else {
    next = null;
  }

  return (
    <div>
      <Header
        name={name}
        subtitle={
          published
            ? "Published. The sponsor sees the reviewed briefing."
            : "Check the review, preview what the sponsor sees, then publish."
        }
      />
      {actionError && (
        <p role="alert" className="mb-3 text-sm text-red-600">
          {actionError}
        </p>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-7">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-1 flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold text-gray-900">
                Publish checklist
              </h3>
              <span className="text-xs text-gray-500">
                {published
                  ? `Published briefing · ${pubLabel(review.published) || planLabel(review, "")}`
                  : `Draft from plan ${planLabel(review, "")}`.trim()}
              </span>
            </div>
            <ul className="divide-y divide-gray-100">
              <CheckRow
                ok={aeDone}
                title={`AE sections verified · ${ae.verified}/${ae.owned}`}
                testId="check-ae"
              >
                {aeDone
                  ? "All AE-owned sections verified."
                  : `Required. Waiting on: ${sectionTitles(data, ae.pending)}.`}
              </CheckRow>
              {/* Not a gate: says when Ask about this plan opens (2026-11-01). */}
              {file?.sponsor_view_status === "ready" && (
                <CheckRow
                  ok={askAboutPlanLine(data, file, isAE).live}
                  title={FLOW.askAboutPlan}
                  testId="check-chat"
                >
                  {askAboutPlanLine(data, file, isAE).text}
                </CheckRow>
              )}
              <CheckRow
                ok={csmDone}
                advisory={!csmDone}
                title={`CSM sections confirmed · ${csm.verified}/${csm.owned}`}
                testId="check-csm"
              >
                {csmDone
                  ? "All CSM-owned sections confirmed."
                  : `Advisory. The AE can publish without it — ${sectionTitles(
                      data,
                      csm.pending
                    )} goes out without the CSM's confirmation.`}
              </CheckRow>
              {/* Only when there are answers (UI tour, 2026-11-03): with none there are
                  no gaps to acknowledge, so no tick; the row below covers it. Its words
                  follow each answer's status: partly answered or not in the plan. */}
              {(review.gaps > 0 || hasAnswers) && (
                <CheckRow
                  ok={gapsOk}
                  title={
                    review.gaps > 0
                      ? `Plan gaps acknowledged · ${gapWords}`
                      : "Plan gaps acknowledged"
                  }
                  testId="check-gaps"
                >
                  {review.gaps === 0 ? (
                    "All Briefing Questions are answered by the plan."
                  ) : (
                    <label
                      className={`mt-1 flex items-center gap-2 ${
                        isAE && !published ? "cursor-pointer" : "opacity-60"
                      }`}
                    >
                      <input
                        id="ack-gaps"
                        data-testid="ack-gaps"
                        type="checkbox"
                        checked={!!review.gaps_acknowledged}
                        disabled={!isAE || !!published || busy}
                        onChange={e => void ackGaps(e.target.checked)}
                        className="focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                      />
                      <span>
                        {`The sponsor will see ${gapSeen}. I'll address ${
                          review.gaps === 1 ? "it" : "them"
                        } in the next plan version.`}
                      </span>
                    </label>
                  )}
                </CheckRow>
              )}
              {noAnswers && (
                <CheckRow
                  ok={noAnswersOk}
                  title="No answers to the Briefing Questions confirmed"
                  testId="check-no-answers"
                >
                  <span className="block">
                    {/* Our failure reads as stalled, with no retry (P8, UI tour item 20). */}
                    {briefingView?.briefing_status === "failed"
                      ? `Answers to the Briefing Questions: ${STALLED_LINE}. You can publish without them.`
                      : "Briefing Questions answers weren't generated for this draft. Use Write the briefing again on Review briefing to add them, or publish without answers."}
                  </span>
                  <label
                    className={`mt-1 flex items-center gap-2 ${
                      isAE && !published ? "cursor-pointer" : "opacity-60"
                    }`}
                  >
                    <input
                      id="ack-no-answers"
                      data-testid="ack-no-answers"
                      type="checkbox"
                      checked={!!review.no_answers_acknowledged}
                      disabled={!isAE || !!published || busy}
                      onChange={e => void ackNoAnswers(e.target.checked)}
                      className="focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                    />
                    <span>
                      The sponsor will see no answers to the Briefing Questions.
                    </span>
                  </label>
                </CheckRow>
              )}
            </ul>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
              {!nav.previewing ? (
                <button
                  type="button"
                  onClick={nav.previewAsSponsor}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                >
                  <Eye className="h-4 w-4" aria-hidden />
                  Preview as sponsor
                </button>
              ) : (
                <span />
              )}
              {published ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800">
                  <BadgeCheck className="h-3.5 w-3.5" aria-hidden />
                  Published {pubLabel(published)} · {shortDate(published.at)}
                </span>
              ) : isAE ? (
                <div className="flex flex-col items-end gap-1">
                  <button
                    type="button"
                    data-testid="publish-btn"
                    disabled={!canPublish || busy}
                    onClick={() => setConfirming(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#974ae4] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Send className="h-4 w-4" aria-hidden />
                    Publish to sponsor
                  </button>
                  {blockers.length > 0 && (
                    <ul className="text-right text-[11px] text-gray-500">
                      {blockers.map(b => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <span
                  data-testid="csm-publish-note"
                  className="flex items-center gap-1.5 text-xs text-gray-500"
                >
                  <Lock className="h-3.5 w-3.5" aria-hidden />
                  Only the AE can publish
                </span>
              )}
            </div>

            {confirming && canPublish && (
              <div
                role="group"
                aria-label="Confirm publish"
                className="mt-4 rounded-lg border border-purple-200 bg-purple-50 p-4 text-sm text-purple-900"
              >
                <div className="font-semibold">
                  Publish {planLabel(review, "this plan")} to the sponsor?
                </div>
                <p className="mt-1 text-xs text-purple-800">
                  {csmDone
                    ? "Every owned section is verified."
                    : `${sectionTitles(data, csm.pending)} will show "CSM confirmation pending".`}{" "}
                  The DRAFT badge is removed and the sponsor sees the reviewed
                  briefing.
                </p>
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    autoFocus
                    disabled={busy}
                    onClick={() => void doPublish()}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#974ae4] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:opacity-50"
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    ) : (
                      <Send className="h-4 w-4" aria-hidden />
                    )}
                    Publish now
                  </button>
                </div>
              </div>
            )}
          </div>

          {published && (
            <div
              data-testid="publish-record"
              aria-live="polite"
              className="rounded-xl border border-emerald-200 bg-emerald-50 p-5"
            >
              <div className="mb-1 text-sm font-semibold text-emerald-900">
                Reviewed and published by {published.by_name || "the AE"} ·{" "}
                {shortDate(published.at)}
              </div>
              <div className="text-xs text-emerald-800">
                {published.csm_pending.length
                  ? `Published without CSM confirmation on: ${sectionTitles(
                      data,
                      published.csm_pending
                    )}. The sponsor sees "CSM confirmation pending" there.`
                  : "All owned sections were verified at publish."}{" "}
                The briefing re-enters review when the next plan version is
                confirmed.
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-5">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-gray-400">
              What {data.sponsors[0]?.name || "the sponsor"} sees
            </div>
            {published ? (
              <div className="mb-3 text-sm text-gray-700">
                Reviewed and published by {published.by_name || "the AE"} ·{" "}
                {shortDate(published.at)}
              </div>
            ) : (
              <>
                <div className="mb-3 text-sm text-gray-700">
                  {`Today: "${underReviewLabel(
                    data,
                    review.review_started_at || review.generated_at
                  )}"`}
                </div>
                <div className="mb-3 text-xs text-gray-500">
                  {`After publishing: "Reviewed and published by ${aeName} · ${todayLabel()}" and the DRAFT badge is removed.`}
                </div>
              </>
            )}
            <ul className="space-y-1.5">
              {ownedSections.map(s => (
                <li
                  key={s.id}
                  className="flex items-center justify-between gap-2 text-xs"
                >
                  <span className="text-gray-700">{s.title}</span>
                  <VerifyMark
                    sid={s.id}
                    owner={owners[s.id]}
                    verification={review.verifications[s.id]}
                  />
                </li>
              ))}
            </ul>
            {/* The version before this one: only while this one isn't out yet
                (once it is, last_published names the version it replaced). */}
            {data.last_published && !published && (
              <div className="mt-3 border-t border-gray-100 pt-3 text-xs text-gray-600">
                Last published: {pubLabel(data.last_published)} ·{" "}
                {shortDate(data.last_published.at)}
              </div>
            )}
          </div>
        </div>
      </div>

      {next}
    </div>
  );
};

export default PublishTab;
